import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { Link, useSearchParams } from 'react-router-dom';
import {
    Heading2,
    TextDefault,
    ThemedButton,
    SpaceBetweenDiv,
} from '@awarego-ui/components';
import ThemedSpinner from '../../components/themed/ThemedSpinner';
import AstPhishingSimulationWizard from './AstPhishingSimulationWizard';
import services from '../../services';
import { useStores } from '../../hooks/use-stores';
import {
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    Box,
    Alert,
} from '@mui/material';

const PAYLOAD_PAGE_SIZE = 20;

function formatWhen(iso) {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return String(iso);
    }
}

/**
 * Map `simulation.trainingSetting` to a compact, colour-coded label.
 *  - microsoftManaged  → green   "Microsoft training"
 *  - custom            → blue    "Custom URL"
 *  - noTraining        → grey    "No training"
 *  - anything else / missing → "—"
 */
function renderTrainingBadge(ts) {
    if (!ts || typeof ts !== 'object') return '—';
    const t =
        String(ts.settingType || '').toLowerCase() ||
        (ts['@odata.type'] || '').toLowerCase();
    if (
        t === 'microsoftmanaged' ||
        t.includes('microsoftmanagedtrainingsetting')
    ) {
        return (
            <Box
                component="span"
                sx={{
                    display: 'inline-block',
                    px: 1,
                    py: 0.25,
                    fontSize: 12,
                    borderRadius: 1,
                    bgcolor: '#e8f5e9',
                    color: '#1b5e20',
                    fontWeight: 600,
                }}
            >
                Microsoft training
            </Box>
        );
    }
    if (t === 'custom' || t.includes('customtrainingsetting')) {
        const url = ts.url || ts.customUrl || '';
        return (
            <Box
                component="span"
                sx={{
                    display: 'inline-block',
                    px: 1,
                    py: 0.25,
                    fontSize: 12,
                    borderRadius: 1,
                    bgcolor: '#e3f2fd',
                    color: '#0d47a1',
                    fontWeight: 600,
                }}
                title={url || ''}
            >
                Custom URL
            </Box>
        );
    }
    if (t === 'notraining' || t.includes('notrainingsetting')) {
        return (
            <Box
                component="span"
                sx={{
                    display: 'inline-block',
                    px: 1,
                    py: 0.25,
                    fontSize: 12,
                    borderRadius: 1,
                    bgcolor: '#f5f5f5',
                    color: '#555',
                    fontWeight: 600,
                }}
            >
                No training
            </Box>
        );
    }
    return ts['@odata.type'] || ts.settingType || '—';
}

function CompanyMicrosoftDefenderAstDashboard({ companyId }) {
    const { commonStore } = useStores();
    const [searchParams, setSearchParams] = useSearchParams();
    /** New instance each call — must memoize or useEffect([load]) loops forever. */
    const svc = useMemo(
        () => services.Companies.microsoftDefenderAstService(companyId),
        [companyId]
    );
    const [loading, setLoading] = useState(true);
    /** True while payloads + simulations + risk + wizard defaults are fetching (after connected). */
    const [microsoftDataLoading, setMicrosoftDataLoading] = useState(false);
    const [status, setStatus] = useState(null);
    const [payloads, setPayloads] = useState([]);
    const [simulations, setSimulations] = useState([]);
    const [risk, setRisk] = useState(null);
    const [payloadQuery, setPayloadQuery] = useState('');
    const [debouncedPayloadSearch, setDebouncedPayloadSearch] = useState('');
    const [payloadPageIndex, setPayloadPageIndex] = useState(0);
    const [payloadNextSkipToken, setPayloadNextSkipToken] = useState(null);
    const [payloadTotalCount, setPayloadTotalCount] = useState(null);
    const [payloadCatalogScope, setPayloadCatalogScope] = useState('both');
    const [payloadBackStack, setPayloadBackStack] = useState([]);
    const [payloadListRefreshing, setPayloadListRefreshing] = useState(false);
    const [payloadRefreshKey, setPayloadRefreshKey] = useState(0);
    const [wizardDefaults, setWizardDefaults] = useState(null);

    useEffect(() => {
        const id = setTimeout(
            () => setDebouncedPayloadSearch(payloadQuery.trim()),
            400
        );
        return () => clearTimeout(id);
    }, [payloadQuery]);

    const [wizardOpen, setWizardOpen] = useState(false);
    const [selectedPayload, setSelectedPayload] = useState(null);

    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewRow, setPreviewRow] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewData, setPreviewData] = useState(null);

    const [simDetailsOpen, setSimDetailsOpen] = useState(false);
    const [simDetailsRow, setSimDetailsRow] = useState(null);
    const [simDetailsLoading, setSimDetailsLoading] = useState(false);
    const [simDetailsData, setSimDetailsData] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setMicrosoftDataLoading(false);
        try {
            const st = await svc.status();
            setStatus(st);
            if (st.connected) {
                setMicrosoftDataLoading(true);
                try {
                    const [s, r, def] = await Promise.all([
                        svc.listSimulations().catch(() => []),
                        svc.riskDashboard().catch(() => null),
                        svc.defaults().catch(() => null),
                    ]);
                    setSimulations(Array.isArray(s) ? s : []);
                    setRisk(r);
                    setWizardDefaults(def);
                    setPayloadRefreshKey((k) => k + 1);
                } finally {
                    setMicrosoftDataLoading(false);
                }
            } else {
                setPayloads([]);
                setPayloadNextSkipToken(null);
                setPayloadTotalCount(null);
                setPayloadCatalogScope('both');
                setPayloadBackStack([]);
                setPayloadPageIndex(0);
                setSimulations([]);
                setRisk(null);
                setWizardDefaults(null);
            }
        } catch (e) {
            commonStore.showMessage(e.message || String(e), 'error');
        } finally {
            setLoading(false);
        }
    }, [svc, commonStore]);

    useEffect(() => {
        if (!status?.connected) {
            return;
        }
        let cancelled = false;
        (async () => {
            setPayloadListRefreshing(true);
            setPayloadBackStack([]);
            setPayloadPageIndex(0);
            try {
                const d = await svc.listPayloadsPage({
                    top: PAYLOAD_PAGE_SIZE,
                    search: debouncedPayloadSearch,
                    scope: 'both',
                    count: '1',
                });
                if (cancelled) return;
                setPayloads(Array.isArray(d.items) ? d.items : []);
                setPayloadNextSkipToken(d.nextSkipToken || null);
                setPayloadTotalCount(
                    typeof d.totalCount === 'number' ? d.totalCount : null
                );
                setPayloadCatalogScope(d.scope || 'both');
            } catch (e) {
                if (cancelled) return;
                commonStore.showMessage(
                    e.message || 'Could not load templates from Microsoft',
                    'error'
                );
                setPayloads([]);
                setPayloadNextSkipToken(null);
            } finally {
                if (!cancelled) setPayloadListRefreshing(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [status?.connected, debouncedPayloadSearch, svc, commonStore, payloadRefreshKey]);

    const goPayloadNext = useCallback(async () => {
        if (!payloadNextSkipToken || payloadListRefreshing || !status?.connected)
            return;
        setPayloadBackStack((s) => [
            ...s,
            { items: payloads, nextSkipToken: payloadNextSkipToken },
        ]);
        setPayloadListRefreshing(true);
        try {
            const d = await svc.listPayloadsPage({
                top: PAYLOAD_PAGE_SIZE,
                skipToken: payloadNextSkipToken,
                search: debouncedPayloadSearch,
                scope: payloadCatalogScope,
                count: '1',
            });
            setPayloads(Array.isArray(d.items) ? d.items : []);
            setPayloadNextSkipToken(d.nextSkipToken || null);
            if (d.scope) setPayloadCatalogScope(d.scope);
            setPayloadPageIndex((i) => i + 1);
        } catch (e) {
            commonStore.showMessage(
                e.message || 'Could not load next page',
                'error'
            );
            setPayloadBackStack((s) => s.slice(0, -1));
        } finally {
            setPayloadListRefreshing(false);
        }
    }, [
        svc,
        status?.connected,
        payloadNextSkipToken,
        payloadListRefreshing,
        payloads,
        debouncedPayloadSearch,
        payloadCatalogScope,
        commonStore,
    ]);

    const goPayloadPrev = useCallback(() => {
        if (payloadBackStack.length === 0 || payloadListRefreshing) return;
        const prev = payloadBackStack[payloadBackStack.length - 1];
        setPayloadBackStack((s) => s.slice(0, -1));
        setPayloads(prev.items);
        setPayloadNextSkipToken(prev.nextSkipToken);
        setPayloadPageIndex((i) => Math.max(0, i - 1));
    }, [payloadBackStack, payloadListRefreshing]);

    const searchParamsKey = searchParams.toString();
    useEffect(() => {
        const message = new URLSearchParams(searchParamsKey).get('message');
        const messageType = new URLSearchParams(searchParamsKey).get(
            'messageType'
        );
        if (!message) return;
        commonStore.showMessage(
            message,
            messageType === 'error' ? 'error' : 'success'
        );
        const next = new URLSearchParams(searchParamsKey);
        next.delete('message');
        next.delete('messageType');
        setSearchParams(next, { replace: true });
    }, [searchParamsKey, setSearchParams, commonStore]);

    useEffect(() => {
        load();
    }, [load]);

    const openPayloadPreview = useCallback(
        async (p) => {
            if (!p || !p.id) return;
            setPreviewRow(p);
            setPreviewData(null);
            setPreviewOpen(true);
            setPreviewLoading(true);
            try {
                const data = await svc.previewPayload(p.id);
                setPreviewData(data);
            } catch (e) {
                commonStore.showMessage(
                    e.message || 'Could not load preview from Microsoft',
                    'error'
                );
                setPreviewOpen(false);
            } finally {
                setPreviewLoading(false);
            }
        },
        [svc, commonStore]
    );

    const closePayloadPreview = () => {
        setPreviewOpen(false);
        setPreviewRow(null);
        setPreviewData(null);
    };

    const openSimulationDetails = useCallback(
        async (s) => {
            if (!s || !s.id) return;
            setSimDetailsRow(s);
            setSimDetailsData(null);
            setSimDetailsOpen(true);
            setSimDetailsLoading(true);
            try {
                const data = await svc.getSimulation(s.id);
                setSimDetailsData(data);
            } catch (e) {
                commonStore.showMessage(
                    e.message ||
                        'Could not load simulation details from Microsoft',
                    'error'
                );
            } finally {
                setSimDetailsLoading(false);
            }
        },
        [svc, commonStore]
    );

    const closeSimulationDetails = () => {
        setSimDetailsOpen(false);
        setSimDetailsRow(null);
        setSimDetailsData(null);
    };

    const onIngest = async () => {
        try {
            const out = await svc.ingest();
            const sims = out && out.simulations != null ? out.simulations : '?';
            const users =
                out && out.userResultRows != null ? out.userResultRows : '?';
            commonStore.showMessage(
                `Synced with Microsoft 365 (${sims} simulations in AwareGO, ${users} participant rows updated).`,
                'success'
            );
            await load();
        } catch (e) {
            commonStore.showMessage(e.message || String(e), 'error');
        }
    };

    const closeWizard = () => {
        setWizardOpen(false);
        setSelectedPayload(null);
    };

    const openWizardForPayload = (payload) => {
        setSelectedPayload(payload);
        setWizardOpen(true);
    };

    if (loading && !status) {
        return <ThemedSpinner />;
    }

    const connected = status && status.connected;
    const consentUrl = `${import.meta.env.REACT_APP_API_URL}/microsoft-defender-ast/authorize/${companyId}`;

    return (
        <div className="top-guide">
            <div className="block new-settings-block">
                <SpaceBetweenDiv>
                    <Heading2 displayBlock>Phishing simulation</Heading2>
                    <Link to="/settings/integrations/microsoft_defender_ast">
                        <ThemedButton
                            size="small"
                            mode="secondary"
                            text="Connection settings"
                        />
                    </Link>
                </SpaceBetweenDiv>
                <TextDefault lighter>
                    Simulations and templates come from Microsoft Attack Simulation
                    Training (Defender Plan 2). New campaigns use a fullscreen wizard:
                    lists from Users, optional landing/login previews, and optional
                    AwareGO training links.
                </TextDefault>
                <br />

                {!connected ? (
                    <div>
                        <TextDefault>
                            Connect Microsoft 365 once so AwareGO can show your
                            simulations and optional PhishGuard correlation.
                        </TextDefault>
                        <br />
                        <br />
                        <a href={consentUrl} target="_blank" rel="noreferrer">
                            <ThemedButton
                                mode="primary"
                                text="Connect Microsoft 365"
                            />
                        </a>
                    </div>
                ) : (
                    <>
                        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                            <TextDefault>
                                <strong>Status:</strong> Connected to Microsoft
                                365.
                            </TextDefault>
                            {status.health && !status.health.ok && (
                                <TextDefault style={{ color: '#c62828' }}>
                                    Microsoft returned an error when checking the
                                    connection: {status.health.error}
                                </TextDefault>
                            )}
                            {status.licenseHint &&
                                status.licenseHint.checked &&
                                !status.licenseHint.hasDefenderForOffice365Plan2 && (
                                    <Alert severity="warning" sx={{ mt: 2 }}>
                                        We did not detect{' '}
                                        <strong>
                                            Microsoft Defender for Office 365
                                            Plan 2
                                        </strong>{' '}
                                        on this tenant. Attack Simulation Training
                                        requires that capability.{' '}
                                        <a
                                            href={
                                                status.licenseHint.learnMoreUrl
                                            }
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            Get started
                                        </a>
                                        {' · '}
                                        <a
                                            href={
                                                status.licenseHint.acquireUrl
                                            }
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            Plans and add-ons
                                        </a>
                                        {' · '}
                                        <a
                                            href={status.licenseHint.trialUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            Try Defender for Office 365
                                        </a>
                                        .
                                    </Alert>
                                )}
                            {status.licenseHint &&
                                !status.licenseHint.checked && (
                                    <Alert severity="info" sx={{ mt: 2 }}>
                                        We could not verify Microsoft 365 licensing
                                        automatically (the Entra app may be missing{' '}
                                        <code>Organization.Read.All</code>). If
                                        simulations fail to load, confirm Plan 2 is
                                        assigned.{' '}
                                        <a
                                            href={
                                                status.licenseHint.learnMoreUrl
                                            }
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            Get started
                                        </a>
                                        .
                                    </Alert>
                                )}
                            <div
                                style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 8,
                                    marginTop: 12,
                                }}
                            >
                                <ThemedButton
                                    mode="primary"
                                    text="Sync with Microsoft 365"
                                    onClick={onIngest}
                                />
                                <ThemedButton
                                    mode="secondary"
                                    text="Reload this page"
                                    onClick={load}
                                    disabled={loading || microsoftDataLoading}
                                />
                            </div>
                            <TextDefault lighter style={{ marginTop: 10 }}>
                                Sync copies simulation lists and participant results
                                into AwareGO for reporting and training links.
                            </TextDefault>
                        </Paper>

                        <Heading2 displayBlock style={{ fontSize: 18 }}>
                            Risk overview
                        </Heading2>
                        <TextDefault lighter style={{ marginBottom: 8 }}>
                            Repeat-offender and coverage data appears after a
                            successful sync. PhishGuard signals show when your
                            PhishGuard integration posts events here.
                        </TextDefault>
                        {risk &&
                        risk.latestRepeatOffenders &&
                        risk.latestRepeatOffenders.captured_at ? (
                            <TextDefault>
                                Last repeat-offender style report from Microsoft:{' '}
                                <strong>
                                    {formatWhen(
                                        risk.latestRepeatOffenders.captured_at
                                    )}
                                </strong>
                            </TextDefault>
                        ) : (
                            <TextDefault lighter>
                                No repeat-offender snapshot yet — run{' '}
                                <strong>Sync with Microsoft 365</strong>.
                            </TextDefault>
                        )}
                        {risk &&
                        risk.recentPhishGuardSignals &&
                        risk.recentPhishGuardSignals.length > 0 && (
                            <ul style={{ marginTop: 8 }}>
                                {risk.recentPhishGuardSignals
                                    .slice(0, 8)
                                    .map((row, i) => (
                                        <li key={`${row.user_email}-${i}`}>
                                            <strong>{row.user_email}</strong> —{' '}
                                            {row.signal_type} (
                                            {formatWhen(row.created_at)})
                                        </li>
                                    ))}
                            </ul>
                        )}
                        {risk &&
                            risk.recentPhishGuardSignals &&
                            risk.recentPhishGuardSignals.length === 0 && (
                                <TextDefault lighter>
                                    No recent PhishGuard signals in AwareGO.
                                </TextDefault>
                            )}

                        {microsoftDataLoading ? (
                            <Box sx={{ py: 5, textAlign: 'center', mt: 2 }}>
                                <ThemedSpinner />
                                <TextDefault
                                    lighter
                                    style={{ marginTop: 16, display: 'block' }}
                                >
                                    Loading templates and simulations from
                                    Microsoft…
                                </TextDefault>
                            </Box>
                        ) : (
                            <>
                        <Heading2
                            displayBlock
                            style={{ fontSize: 18, marginTop: 28 }}
                        >
                            Templates from Microsoft (payloads)
                        </Heading2>
                        <TextDefault lighter style={{ marginBottom: 8 }}>
                            Official catalog from Microsoft 365 (server-side pages of{' '}
                            {PAYLOAD_PAGE_SIZE}). Search runs against template display names
                            in Graph.
                            {payloadTotalCount != null && (
                                <>
                                    {' '}
                                    <strong>{payloadTotalCount}</strong> match
                                    {payloadTotalCount === 1 ? '' : 'es'} your current
                                    search.
                                </>
                            )}
                            {payloadCatalogScope === 'global' && (
                                <>
                                    {' '}
                                    Showing the <strong>global</strong> catalog only;
                                    tenant-specific templates may be unavailable for this
                                    tenant.
                                </>
                            )}
                        </TextDefault>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Search by display name (Microsoft Graph)…"
                            value={payloadQuery}
                            onChange={(e) => setPayloadQuery(e.target.value)}
                            margin="dense"
                        />
                        {payloadListRefreshing && (
                            <TextDefault lighter style={{ marginTop: 4 }}>
                                Updating list…
                            </TextDefault>
                        )}
                        <Table
                            size="small"
                            sx={{
                                mt: 1,
                                opacity: payloadListRefreshing ? 0.55 : 1,
                            }}
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Technique</TableCell>
                                    <TableCell>Language</TableCell>
                                    <TableCell>Source</TableCell>
                                    <TableCell align="right">Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {payloads.length === 0 && !payloadListRefreshing ? (
                                    <TableRow>
                                        <TableCell colSpan={5}>
                                            <TextDefault lighter>
                                                No templates on this page. Adjust search
                                                or use Next/Previous, or check Defender
                                                Plan 2 and permissions.
                                            </TextDefault>
                                        </TableCell>
                                    </TableRow>
                                ) : payloads.length === 0 && payloadListRefreshing ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center">
                                            <ThemedSpinner />
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    payloads.map((p, idx) => (
                                        <TableRow key={p.id || p.name || idx}>
                                            <TableCell>
                                                {p.name ||
                                                    p.displayName ||
                                                    p.id}
                                            </TableCell>
                                            <TableCell>
                                                {p.attackTechnique || '—'}
                                            </TableCell>
                                            <TableCell>
                                                {p.language ||
                                                    p.locale ||
                                                    '—'}
                                            </TableCell>
                                            <TableCell>{p.source || '—'}</TableCell>
                                            <TableCell align="right">
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        gap: 0.5,
                                                        justifyContent: 'flex-end',
                                                        flexWrap: 'wrap',
                                                    }}
                                                >
                                                    <ThemedButton
                                                        size="small"
                                                        mode="secondary"
                                                        text="Preview"
                                                        onClick={() =>
                                                            openPayloadPreview(p)
                                                        }
                                                    />
                                                    <ThemedButton
                                                        size="small"
                                                        mode="secondary"
                                                        text="Create campaign…"
                                                        onClick={() =>
                                                            openWizardForPayload(
                                                                p
                                                            )
                                                        }
                                                    />
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mt: 1,
                                flexWrap: 'wrap',
                            }}
                        >
                            <TextDefault lighter>
                                Page {payloadPageIndex + 1}
                                {payloadTotalCount != null
                                    ? ` · ${payloadTotalCount} total (search)`
                                    : ''}{' '}
                                · {PAYLOAD_PAGE_SIZE} per request
                            </TextDefault>
                            <ThemedButton
                                size="small"
                                mode="secondary"
                                text="Previous"
                                disabled={
                                    payloadBackStack.length === 0 ||
                                    payloadListRefreshing
                                }
                                onClick={goPayloadPrev}
                            />
                            <ThemedButton
                                size="small"
                                mode="secondary"
                                text="Next"
                                disabled={
                                    !payloadNextSkipToken || payloadListRefreshing
                                }
                                onClick={goPayloadNext}
                            />
                        </Box>

                        <Heading2
                            displayBlock
                            style={{ fontSize: 18, marginTop: 28 }}
                        >
                            Simulations in Microsoft 365
                        </Heading2>
                        <TextDefault lighter style={{ marginBottom: 8 }}>
                            Every simulation visible in your tenant (Defender,
                            AwareGO, or elsewhere).
                        </TextDefault>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Launch</TableCell>
                                    <TableCell>Technique</TableCell>
                                    <TableCell>Training</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {simulations.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5}>
                                            <TextDefault lighter>
                                                No simulations returned from
                                                Microsoft. Run Sync or verify
                                                permissions.
                                            </TextDefault>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    simulations.map((s) => (
                                        <TableRow
                                            key={s.id}
                                            hover
                                            onClick={() =>
                                                openSimulationDetails(s)
                                            }
                                            sx={{ cursor: 'pointer' }}
                                        >
                                            <TableCell>
                                                {s.displayName || s.id}
                                            </TableCell>
                                            <TableCell>{s.status || '—'}</TableCell>
                                            <TableCell>
                                                {formatWhen(s.launchDateTime)}
                                            </TableCell>
                                            <TableCell>
                                                {s.attackTechnique || '—'}
                                            </TableCell>
                                            <TableCell>
                                                {renderTrainingBadge(
                                                    s.trainingSetting
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                            </>
                        )}
                    </>
                )}
            </div>

            <AstPhishingSimulationWizard
                open={wizardOpen}
                onClose={closeWizard}
                companyId={companyId}
                svc={svc}
                initialPayload={selectedPayload}
                wizardDefaults={wizardDefaults}
                onCreated={load}
            />

            <Dialog
                open={previewOpen}
                onClose={closePayloadPreview}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Template preview</DialogTitle>
                <DialogContent dividers>
                    {previewLoading && (
                        <Box sx={{ py: 4, textAlign: 'center' }}>
                            <ThemedSpinner />
                        </Box>
                    )}
                    {!previewLoading && previewRow && (
                        <Box>
                            {(() => {
                                const p =
                                    (previewData && previewData.payload) ||
                                    previewRow;
                                const d = previewData && previewData.detail;
                                return (
                                    <>
                                        <TextDefault>
                                            <strong>
                                                {p.name ||
                                                    p.displayName ||
                                                    p.id}
                                            </strong>
                                        </TextDefault>
                                        {p.description && (
                                            <TextDefault
                                                lighter
                                                style={{ marginTop: 8 }}
                                            >
                                                {p.description}
                                            </TextDefault>
                                        )}
                                        <TextDefault
                                            lighter
                                            style={{ marginTop: 8 }}
                                        >
                                            Technique: {p.attackTechnique || '—'}{' '}
                                            · Language:{' '}
                                            {p.language || p.locale || '—'} · Source:{' '}
                                            {p.source || '—'}
                                        </TextDefault>
                                        {!d && (
                                            <TextDefault
                                                lighter
                                                style={{ marginTop: 16 }}
                                            >
                                                Microsoft did not return extended
                                                preview content for this template (the
                                                detail API may be unavailable for some
                                                catalog entries).
                                            </TextDefault>
                                        )}
                                        {d && (
                                            <Box sx={{ mt: 2 }}>
                                                <TextDefault>
                                                    <strong>From:</strong>{' '}
                                                    {d.fromName || '—'} —{' '}
                                                    {d.fromEmail || '—'}
                                                </TextDefault>
                                                <TextDefault
                                                    style={{ marginTop: 8 }}
                                                >
                                                    <strong>Subject:</strong>{' '}
                                                    {d.subject || '—'}
                                                </TextDefault>
                                                {d.phishingUrl && (
                                                    <TextDefault
                                                        style={{ marginTop: 8 }}
                                                    >
                                                        <strong>Sample link:</strong>{' '}
                                                        <a
                                                            href={d.phishingUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            {d.phishingUrl}
                                                        </a>
                                                    </TextDefault>
                                                )}
                                                {d.content && (
                                                    <>
                                                        <TextDefault
                                                            style={{
                                                                marginTop: 16,
                                                                marginBottom: 4,
                                                            }}
                                                        >
                                                            <strong>
                                                                Message body (from
                                                                Microsoft)
                                                            </strong>
                                                        </TextDefault>
                                                        <TextDefault
                                                            lighter
                                                            style={{ marginBottom: 8 }}
                                                        >
                                                            Shown in a sandboxed frame
                                                            (scripts disabled). Content
                                                            comes from your Microsoft 365
                                                            Attack Simulation Training
                                                            catalog.
                                                        </TextDefault>
                                                        <iframe
                                                            title="Payload email HTML preview"
                                                            sandbox=""
                                                            srcDoc={d.content}
                                                            style={{
                                                                width: '100%',
                                                                height: 380,
                                                                border: '1px solid rgba(0,0,0,0.12)',
                                                                borderRadius: 4,
                                                            }}
                                                        />
                                                    </>
                                                )}
                                                {Array.isArray(d.coachMarks) &&
                                                    d.coachMarks.length > 0 && (
                                                        <Box sx={{ mt: 2 }}>
                                                            <TextDefault>
                                                                <strong>
                                                                    Teaching points
                                                                </strong>
                                                            </TextDefault>
                                                            <ul
                                                                style={{
                                                                    marginTop: 8,
                                                                    paddingLeft: 20,
                                                                }}
                                                            >
                                                                {d.coachMarks.map(
                                                                    (cm, i) => (
                                                                        <li
                                                                            key={`${cm.order}-${i}`}
                                                                        >
                                                                            <strong>
                                                                                {cm.indicator ||
                                                                                    '—'}
                                                                                :
                                                                            </strong>{' '}
                                                                            {cm.description ||
                                                                                ''}
                                                                        </li>
                                                                    )
                                                                )}
                                                            </ul>
                                                        </Box>
                                                    )}
                                            </Box>
                                        )}
                                    </>
                                );
                            })()}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <ThemedButton
                        mode="secondary"
                        text="Close"
                        onClick={closePayloadPreview}
                    />
                </DialogActions>
            </Dialog>

            <Dialog
                open={simDetailsOpen}
                onClose={closeSimulationDetails}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Simulation details
                    {simDetailsRow ? (
                        <TextDefault
                            lighter
                            style={{ marginTop: 4, fontSize: 13 }}
                        >
                            {simDetailsRow.displayName || simDetailsRow.id}
                        </TextDefault>
                    ) : null}
                </DialogTitle>
                <DialogContent dividers>
                    {simDetailsLoading && (
                        <Box sx={{ py: 4, textAlign: 'center' }}>
                            <ThemedSpinner />
                        </Box>
                    )}
                    {!simDetailsLoading && simDetailsData && (
                        <Box>
                            <Box sx={{ mb: 2 }}>
                                <TextDefault>
                                    <strong>Status:</strong>{' '}
                                    {simDetailsData.status || '—'} ·{' '}
                                    <strong>Technique:</strong>{' '}
                                    {simDetailsData.attackTechnique || '—'} ·{' '}
                                    <strong>Type:</strong>{' '}
                                    {simDetailsData.attackType || '—'}
                                </TextDefault>
                                <TextDefault
                                    lighter
                                    style={{ marginTop: 4 }}
                                >
                                    <strong>Created:</strong>{' '}
                                    {formatWhen(
                                        simDetailsData.createdDateTime
                                    )}{' '}
                                    · <strong>Launch:</strong>{' '}
                                    {formatWhen(
                                        simDetailsData.launchDateTime
                                    )}{' '}
                                    · <strong>Duration:</strong>{' '}
                                    {simDetailsData.durationInDays || '—'}d
                                </TextDefault>
                                <TextDefault
                                    lighter
                                    style={{ marginTop: 4, fontSize: 12 }}
                                >
                                    Graph id:{' '}
                                    <code>{simDetailsData.id}</code>
                                </TextDefault>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <TextDefault style={{ marginBottom: 4 }}>
                                    <strong>Training setting</strong>{' '}
                                    {renderTrainingBadge(
                                        simDetailsData.trainingSetting
                                    )}
                                </TextDefault>
                                {simDetailsData.trainingSetting &&
                                simDetailsData.trainingSetting.url ? (
                                    <TextDefault
                                        lighter
                                        style={{ fontSize: 13 }}
                                    >
                                        Custom training URL:{' '}
                                        <a
                                            href={
                                                simDetailsData.trainingSetting
                                                    .url
                                            }
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {
                                                simDetailsData.trainingSetting
                                                    .url
                                            }
                                        </a>
                                    </TextDefault>
                                ) : null}
                                <pre
                                    style={{
                                        background: '#f7f7fa',
                                        padding: 10,
                                        borderRadius: 4,
                                        fontSize: 12,
                                        marginTop: 6,
                                        maxHeight: 160,
                                        overflow: 'auto',
                                    }}
                                >
                                    {JSON.stringify(
                                        simDetailsData.trainingSetting || {},
                                        null,
                                        2
                                    )}
                                </pre>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <TextDefault style={{ marginBottom: 4 }}>
                                    <strong>End-user notification</strong>
                                </TextDefault>
                                <pre
                                    style={{
                                        background: '#f7f7fa',
                                        padding: 10,
                                        borderRadius: 4,
                                        fontSize: 12,
                                        maxHeight: 200,
                                        overflow: 'auto',
                                    }}
                                >
                                    {JSON.stringify(
                                        simDetailsData.endUserNotificationSetting ||
                                            {},
                                        null,
                                        2
                                    )}
                                </pre>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <TextDefault style={{ marginBottom: 4 }}>
                                    <strong>Payload</strong>
                                </TextDefault>
                                {simDetailsData.payload ? (
                                    <TextDefault
                                        lighter
                                        style={{ fontSize: 13 }}
                                    >
                                        {simDetailsData.payload.displayName ||
                                            simDetailsData.payload.name ||
                                            '—'}{' '}
                                        · source:{' '}
                                        {simDetailsData.payload.source || '—'}{' '}
                                        · language:{' '}
                                        {simDetailsData.payload.language ||
                                            simDetailsData.payload.locale ||
                                            '—'}
                                    </TextDefault>
                                ) : (
                                    <TextDefault lighter>
                                        Not expanded (only bind id present).
                                    </TextDefault>
                                )}
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <TextDefault style={{ marginBottom: 4 }}>
                                    <strong>Landing page</strong>
                                </TextDefault>
                                {simDetailsData.landingPage ? (
                                    <TextDefault
                                        lighter
                                        style={{ fontSize: 13 }}
                                    >
                                        {simDetailsData.landingPage
                                            .displayName ||
                                            simDetailsData.landingPage.id}{' '}
                                        · source:{' '}
                                        {simDetailsData.landingPage.source ||
                                            '—'}{' '}
                                        · locale:{' '}
                                        {simDetailsData.landingPage.locale ||
                                            '—'}
                                    </TextDefault>
                                ) : (
                                    <TextDefault lighter>
                                        Not expanded (only bind id present).
                                    </TextDefault>
                                )}
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <TextDefault style={{ marginBottom: 4 }}>
                                    <strong>Login page</strong>
                                </TextDefault>
                                {simDetailsData.loginPage ? (
                                    <TextDefault
                                        lighter
                                        style={{ fontSize: 13 }}
                                    >
                                        {simDetailsData.loginPage
                                            .displayName ||
                                            simDetailsData.loginPage.id}{' '}
                                        · source:{' '}
                                        {simDetailsData.loginPage.source ||
                                            '—'}
                                    </TextDefault>
                                ) : (
                                    <TextDefault lighter>
                                        Not expanded (only bind id present, or
                                        not applicable for this technique).
                                    </TextDefault>
                                )}
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <ThemedButton
                        mode="secondary"
                        text="Close"
                        onClick={closeSimulationDetails}
                    />
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default observer(CompanyMicrosoftDefenderAstDashboard);
